import { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { type RootState } from '#root//rootState';
import { selectContextAdminOptions, selectContextFeatures } from '@/common/application/contextSlice';
import { isAccessProfileEnabled } from '@/common/application/featureGating';
import { useGroup } from '@/group/application/groupController';

/**
 * Whether the end-user "My access" (reservation profiles / pin codes) feature is available.
 *
 * Strict AND: the subscription (reservation class) must allow access profiles, AND it must let end
 * users manage their profiles from the web (`adminOptions.enableWebAccess === true` — this app IS
 * that web surface, and this layer alone is read strictly with no default fallback), AND at least one
 * of the user's groups must have a PACKET that also allows them. The two `enableAccessProfile` layers
 * resolve against their spec defaults (both declared `true`), so a record that never stored the flag
 * is not treated as a denial. The packet lives on the user's (sub)group — NOT on the company
 * (`context.superGroup`), which carries no packet — so we read it from `state.groups.list` (the groups
 * loaded for this user). See `isAccessProfileEnabled` for the layer-by-layer rationale.
 */
export const useIsAccessProfileEnabled = (): boolean => {
    const subFeatures = useSelector(selectContextFeatures);
    const adminOptions = useSelector(selectContextAdminOptions);
    const groups = useSelector((state: RootState) => state.groups.list);
    const superGroupPacket = useSelector(
        (state: RootState) => state.context.superGroup?.packet?.config?.features,
    );

    const packetSources = [superGroupPacket, ...groups.map((g) => g.packet?.config?.features)];
    // Strict AND per packet source: the subscription (features + web access) AND at least one group
    // packet must enable it.
    return packetSources.some((packetFeatures) =>
        isAccessProfileEnabled(subFeatures, packetFeatures, adminOptions));
};

/**
 * Whether an access profile carries a pin code at all: only parkings whose system asks for one in
 * addition to the plate number (`operations.pinCodeRequired`, edited in the parking properties
 * screen) show / require the `secretCode`. The legacy top-level `pincodeRequired` is read as a
 * fallback because the API type still declares it. Fail-closed: an unset flag means no pin code, so
 * a parking that never stored it does not block saving a profile on a field it cannot use.
 */
export const useIsPinCodeRequired = (): boolean => useSelector(
    (state: RootState) =>
        state.context.parking?.operations?.pinCodeRequired ?? state.context.parking?.pincodeRequired ?? false,
);

/**
 * Route guard for the "My access" screens: loads the groups the gate reads, then sends the user back
 * to the dashboard once the feature is known to be unavailable. Returns the resolved gate value.
 *
 * Routes carry no feature or role guard (see `navigator.tsx`), so without this a direct URL reaches
 * the screens even when the sidebar entry is hidden. The redirect waits for the groups fetch to
 * SETTLE rather than testing `groups.length > 0`: during the initial (empty) load the gate reads as
 * disabled and would bounce a legitimate user, while a user with no group at all would never be
 * redirected. A failed fetch also counts as settled — entitlement cannot be confirmed, and this gate
 * is deliberately fail-closed.
 *
 * The fetch is SKIPPED when the store already holds groups: arriving from the dashboard, they were
 * loaded there (with the same deep-packet populate this gate needs), so re-fetching only duplicates
 * the request. It still runs on a hard refresh or a direct URL, where nothing has populated the list.
 */
export const useRequireAccessProfile = (): boolean => {
    const navigate = useNavigate();
    const { getGroups } = useGroup();
    const accessEnabled = useIsAccessProfileEnabled();
    const groupsLoaded = useSelector((state: RootState) => state.groups.list.length > 0);
    const [groupsChecked, setGroupsChecked] = useState(false);

    useEffect(() => {
        // Read at mount only: a list already populated needs no fetch, and this must not re-run when
        // the fetch below fills it in.
        if (groupsLoaded) {
            setGroupsChecked(true);
            return;
        }
        void getGroups()
            .catch(() => undefined)
            .finally(() => setGroupsChecked(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (groupsChecked && !accessEnabled) {
            navigate('/', { replace: true });
        }
    }, [groupsChecked, accessEnabled, navigate]);

    return accessEnabled;
};
